import { Component, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

class RouteErrorBoundaryClass extends Component<Props & { t: (key: string) => string }, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk');

    return { hasError: true, isChunkError };
  }

  handleRetry = () => {
    if (this.state.isChunkError) {
      // For chunk errors, reload the page to get fresh chunks
      window.location.reload();
    } else {
      this.setState({ hasError: false, isChunkError: false });
    }
  };

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
          <Typography variant="h6" gutterBottom>
            {this.state.isChunkError
              ? t('errors.connectionIssue')
              : t('errors.somethingWentWrong')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('errors.tryAgain')}
          </Typography>
          <Button variant="contained" onClick={this.handleRetry}>
            {t('errors.retry')}
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Wrapper to use hooks with class component
export default function RouteErrorBoundary({ children }: Props) {
  const { t } = useTranslation();
  return <RouteErrorBoundaryClass t={t}>{children}</RouteErrorBoundaryClass>;
}
